import { IonText, useIonModal, useIonToast } from '@ionic/react';
import classNames from 'classnames';
import { AllowedBusinessTypes, Business, Item, isBusinessSubmitter, objectEquals } from 'model';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import {
	useDeleteBusinessMutation,
	useGetBusinessQuery,
	useLazySearchBusinessByWebsiteQuery,
	useLazySearchBusinessQuery,
	useUpdateBusinessMutation
} from '../api/BusinessApi';
import { BusinessHeader } from '../components/BusinessHeader';
import { EditBusinessImage } from '../components/EditBusinessImage';
import { Checkbox } from '../components/Forms/Checkbox';
import { FormInput } from '../components/Forms/FormInput';
import { FormInputList } from '../components/Forms/FormInputList';
import { FormSelectBox } from '../components/Forms/FormSelectBox';
import { FormTextarea } from '../components/Forms/FormTextarea';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { ProfileImage } from '../components/ProfileImage';
import { IconCheck, IconClose } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { BusinessEditRules, SubmitBusinessEditRules } from '../formConfigs/EditBusinessFieldsConfig';
import { useCurrentUser } from '../hooks/useCurrentUser';
import {
	FieldValuesType,
	FormValueType,
	SetValuesCallback,
	SetValuesFunction,
	useFormValues
} from '../hooks/useFormValues';
import { useQueryWithDebounce } from '../hooks/useQueryWithDebounce';
import { useLoading } from '../hooks/useQuirkLoading';
import { isMutationError } from '../util/error';
import { getBusinessImages } from '../util/getBusinessImages';
import { parseHoursOfOperation } from '../util/hoursOfOperation';
import { getInitials } from '../util/initials';
import styles from './UserEditBusiness.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import formStyles from '../components/Forms/FormFields.module.css';

export type BusinessWithLists = Omit<Business, 'hours' | 'items'> & {
	hours: string[];
	items: string[];
	isOwner?: boolean;
};

export const UserEditBusiness: React.FC = () => {
	const user = useCurrentUser();
	const { businessid } = useParams<{ businessid: string }>();
	const { data, isLoading } = useGetBusinessQuery(businessid);
	useLoading({
		isLoading
	});
	const [updateBusiness, { isLoading: isUpdating }] = useUpdateBusinessMutation();
	const [deleteBusiness] = useDeleteBusinessMutation();
	const isSubmitter = data && isBusinessSubmitter(user, data);
	const form = useFormValues(isSubmitter ? SubmitBusinessEditRules : BusinessEditRules, data, objectEquals);
	const { setValues, formValues, stateDataObject: business, validateForm } = form;
	const hoursFieldValue: FieldValuesType<BusinessWithLists['hours']> = {
		value: parseHoursOfOperation(formValues?.hours?.value ?? ''),
		errors: formValues?.hours?.errors ?? []
	};
	const hoursSetValues: SetValuesFunction<BusinessWithLists> = (
		value: FormValueType<BusinessWithLists> | SetValuesCallback<BusinessWithLists>
	) => {
		if (typeof value === 'function') {
			setValues({ hours: value({ hours: hoursFieldValue }).hours?.join(',') ?? '' });
		} else {
			setValues({ hours: value.hours?.join(',') ?? '' });
		}
	};
	const itemsFieldValue: FieldValuesType<BusinessWithLists['items']> = {
		value: formValues?.items?.value?.map(({ name }: Item) => name) ?? [],
		errors: formValues?.items?.errors ?? []
	};
	const itemsSetValues: SetValuesFunction<BusinessWithLists> = (
		value: FormValueType<BusinessWithLists> | SetValuesCallback<BusinessWithLists>
	) => {
		if (typeof value === 'function') {
			setValues({
				items: value({ items: itemsFieldValue }).items?.map(item => ({ type: 'item', name: item })) ?? []
			});
		} else {
			setValues({ items: value.items?.map(item => ({ type: 'item', name: item })) ?? [] });
		}
	};
	const history = useHistory();
	const onCancel = async () => {
		if (data?.draft) {
			await deleteBusiness(businessid);
		}
		if (data) {
			setValues(data);
		}
		history.goBack();
	};
	const [showToast] = useIonToast();
	const onSubmit = async () => {
		if (business) {
			const submitData: Business & { isOwner?: boolean } = { ...business, draft: false };
			delete submitData.isOwner;
			if (!validateForm()) {
				showToast({
					color: 'danger',
					message: 'Please fix errors',
					position: 'top',
					duration: 1500
				});
				return;
			}
			const result = await updateBusiness({ business: submitData });
			if (isMutationError(result)) {
				showToast({
					color: 'danger',
					message: 'Error updating business',
					position: 'top',
					duration: 1500
				});
			} else {
				showToast({
					color: 'success',
					message: data?.draft ? 'Thank you for your suggestion' : 'Business has been updated',
					position: 'top',
					duration: 1500
				});
			}
		}
		history.goBack();
	};

	useEffect(() => {
		if (data !== undefined) {
			// Keep the formValues in sync with the server state as editing images can cause them to be out of sync
			setValues({
				banner: data.banner,
				logo: data.logo
			});
		}
	}, [data, setValues]);

	const [showEditLogo, hideEditLogo] = useIonModal(EditBusinessImage, {
		image: 'logo',
		onDoneClick: () => hideEditLogo(),
		form
	});

	const [showEditBanner, hideEditBanner] = useIonModal(EditBusinessImage, {
		image: 'cover',
		onDoneClick: () => hideEditBanner(),
		form
	});

	const {
		trigger: websiteTrigger,
		data: websiteSearchData,
		originalArgs: websiteOriginalArgs
	} = useQueryWithDebounce(useLazySearchBusinessByWebsiteQuery, 300);
	const {
		trigger: nameTrigger,
		data: nameSearchData,
		originalArgs: nameOriginalArgs
	} = useQueryWithDebounce(useLazySearchBusinessQuery, 300);

	useEffect(() => {
		if (
			business !== undefined &&
			business?.website &&
			business?.website.length > 1 &&
			business?.website !== websiteOriginalArgs
		) {
			websiteTrigger(business?.website);
		}
	}, [business, websiteTrigger, websiteOriginalArgs]);
	useEffect(() => {
		if (
			business !== undefined &&
			business?.name &&
			business?.name.length > 3 &&
			business.name !== nameOriginalArgs?.search
		) {
			nameTrigger({ search: business.name, businessType: 'all' });
		}
	}, [business, nameTrigger, nameOriginalArgs?.search]);
	return (
		<QuirkIonPage name="Edit Business" data={{ business: businessid }} roles={['user']}>
			<Header
				title={data && (data.draft ? 'Suggest Business' : 'Edit Business')}
				left={data && <IconButton icon={<IconClose />} onClick={onCancel} />}
				right={
					data && (
						<IconButton
							fill="solid"
							label="Done"
							icon={<IconCheck />}
							onClick={onSubmit}
							disabled={isLoading || isUpdating}
						/>
					)
				}
			/>
			<QuirkIonContent flush>
				<div className={styles.UserEditBusiness}>
					{business && data && (
						<>
							<BusinessHeader
								business={business}
								onEditBanner={showEditBanner}
								onEditLogo={showEditLogo}
							/>

							<div className={styles.fieldsWrapper}>
								{isSubmitter && (
									<div className={formStyles.fieldContainer}>
										<Checkbox
											label="This is my business"
											value={business.owner === user._id && business.claimStatus !== 'unclaimed'}
											onClick={e => {
												setValues({
													owner: e.target.checked ? user._id : '',
													claimStatus: e.target.checked ? 'pending' : 'unclaimed'
												});
											}}
										/>
									</div>
								)}
								<FormInput
									id="website"
									setValues={setValues}
									field={formValues?.website}
									label="Website"
								/>
								{isSubmitter &&
								business.website.length > 1 &&
								websiteSearchData &&
								websiteSearchData.length > 0 ? (
									<div className={styles.searchWrapper}>
										<IonText className={classNames(styles.searchText, styles.warning)}>
											<>
												{`${websiteSearchData?.length} other business${
													websiteSearchData?.length !== 1 ? 'es use' : ' uses'
												} this same website`}
											</>
										</IonText>
									</div>
								) : null}
								<FormInput id="name" setValues={setValues} field={formValues?.name} label="Name" />
								{isSubmitter && business.name?.length > 3 ? (
									<div className={styles.searchWrapper}>
										<IonText
											className={classNames(
												styles.searchText,
												nameSearchData?.length !== 0 ? styles.warning : styles.ok
											)}
										>
											{nameSearchData !== undefined && (
												<>
													{`Found ${nameSearchData.length} potential duplicate${
														nameSearchData.length !== 1 ? 's' : ''
													}`}
												</>
											)}
										</IonText>
										<div className={styles.searchResults}>
											{nameSearchData?.map((item: Business) => (
												<div className={styles.searchResult} key={item._id}>
													<ProfileImage
														url={getBusinessImages(item).logo}
														text={getInitials(item.name)}
														size="small"
														shape="square"
														border
														background={item.logoBackgroundColor ?? 'transparent'}
														contain
													/>
													{item.name}
												</div>
											))}
										</div>
									</div>
								) : null}
								<FormTextarea
									id="description"
									setValues={setValues}
									field={formValues?.description}
									label="Description"
								/>
								<FormSelectBox
									id="businessType"
									setValues={setValues}
									field={formValues?.businessType}
									label="Business Type"
									options={AllowedBusinessTypes}
								/>
								<FormInput
									id="address"
									setValues={setValues}
									field={formValues?.address}
									label="Address"
								/>
								<FormInput
									id="district"
									setValues={setValues}
									field={formValues?.district}
									label="District"
								/>
								<FormInput id="city" setValues={setValues} field={formValues?.city} label="City" />
								<FormInput id="phone" setValues={setValues} field={formValues?.phone} label="Phone" />
								<FormInputList
									id="hours"
									setValues={hoursSetValues}
									field={hoursFieldValue}
									label="Hours"
									placeholder="Mon-Fri 10a-5p"
								/>
								<FormInputList
									id="items"
									setValues={itemsSetValues}
									field={itemsFieldValue}
									label="Items for sale or services"
									wrapItems
								/>
							</div>
						</>
					)}
				</div>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};
